import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AgaveAuthService } from 'tq-agave';
import { AuthService } from '../auth.service';
import { GlobalsService } from '../../../globals.service';
import { TranslocoService } from '@jsverse/transloco';
import { UserService } from 'app/core/user/user.service';

/**
 * Restituisce una guardia che controlla che l'utente possa accedere alla pagina.
 *
 * Controlla con queste priorità:
 * 1. ticket nell'url (queryParams): se trova il ticket nei queryParams, effettua una login, eventualmente sostituendo il ticket attuale con quello dei query params
 * 2. ticket in memoria: controlla i boolean parameter e decide se accettare o no
 * 3. ticket nel local storage: tenta una autenticazione di default
 *
 * */
export const clearanceGuard: (clearance: string) => CanActivateFn = clearance => {
    return async (route, state) => {
        const appAuthService = inject(AuthService);
        const agaveAuthService = inject(AgaveAuthService);
        const router = inject(Router);
        const globalsService = inject(GlobalsService);
        const translocoService = inject(TranslocoService);
        // vince il ticket nei queryParams
        if ('ticket' in route.queryParams) {
            const ticket = route.queryParams['ticket'];
            await agaveAuthService.logOut();
            const rememberMe = route.queryParams['rememberMe'] ?? false;
            await agaveAuthService.authenticateFromTicket(ticket, rememberMe);
        }
        // Check the authentication status
        // Authservice.check fa anche l'autologin con il ticket nello storage
        // IMPORTANTE! Serve la clearance reader per avere authenticated == true!
        const authenticated = await appAuthService.check();
        if (!authenticated) {
            // Redirect to the sign-in page with a redirectUrl param
            const redirectURL = state.url === '/sign-out' ? '' : `redirectURL=${state.url}`;
            const urlTree = router.parseUrl(`sign-in?${redirectURL}`);
            return urlTree;
        }
        // se l'utente è autenticato ma non autorizzato, semplicemente blocco
        //  la navigazione senza redirect
        const canActivate = agaveAuthService.getBooleanParameterValue(clearance) ?? false;
        if (canActivate) {
            return true;
        } else {
            const description = translocoService.translate('user-not-authorised-error', {
                'clearance': clearance
            });
            globalsService.openErrorDialogString(description, 'authorisation-error');
            return false;
        }
    }
}

export const ReaderGuard = clearanceGuard('reader');
export const WriterGuard = clearanceGuard('writer');
export const AdminGuard = clearanceGuard('administrator');
export const CompanyManagerGuard = clearanceGuard('company_manager');
